export enum IntegrationStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  PENDING = 'pending',
  ERROR = 'error',
  CONFIGURING = 'configuring',
}

export enum IntegrationInstanceStatus {
  UNCONFIGURED = 'unconfigured',
  CONFIGURING = 'configuring',
  ACTIVE = 'active',
  DISABLED = 'disabled',
}

export enum IntegrationType {
  PAYMENT = 'payment',
  EMAIL = 'email',
  SMS = 'sms',
  ANALYTICS = 'analytics',
  CRM = 'crm',
  MARKETING = 'marketing',
  STORAGE = 'storage',
  API = 'api',
  OTHER = 'other',
}

export enum IntegrationCategory {
  CORE = 'core',
  BUSINESS = 'business',
  CUSTOM = 'custom',
}

export interface ModuleInfo {
  id: string;
  name: string;
  key: string;
  description: string;
  icon: string;
  color: string;
  status: string;
}

export interface Integration {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  type: IntegrationType;
  status: IntegrationStatus;
  category: IntegrationCategory;
  modules?: ModuleInfo[];
  providerKey: string;
  capabilities?: string[];
  isSystemIntegration: boolean;
  config?: Record<string, any>;
  technicalConfig?: Record<string, any>;
  businessConfig?: Record<string, any>;
  configVersion?: string;
  apiKey?: string;
  apiSecret?: string;
  webhookUrl?: string;
  lastSync?: Date;
  lastError?: string;
  isActive: boolean;
  metadata?: Record<string, any>;
  instanceStatus: IntegrationInstanceStatus;
  configuredAt?: Date;
  configuredBy?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateIntegrationDto {
  name: string;
  description?: string;
  type: IntegrationType;
  status?: IntegrationStatus;
  config?: Record<string, any>;
  technicalConfig?: Record<string, any>;
  businessConfig?: Record<string, any>;
  configVersion?: string;
  apiKey?: string;
  apiSecret?: string;
  webhookUrl?: string;
  metadata?: Record<string, any>;
  isActive?: boolean;
  moduleIds?: string[];
}

export interface UpdateIntegrationDto {
  name?: string;
  description?: string;
  type?: IntegrationType;
  status?: IntegrationStatus;
  config?: Record<string, any>;
  technicalConfig?: Record<string, any>;
  businessConfig?: Record<string, any>;
  configVersion?: string;
  apiKey?: string;
  apiSecret?: string;
  webhookUrl?: string;
  metadata?: Record<string, any>;
  isActive?: boolean;
  moduleIds?: string[];
}

export interface IntegrationUserConfig {
  id: string;
  userId: string;
  config: Record<string, any>;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FindIntegrationsDto {
  page?: number;
  limit?: number;
  search?: string;
  type?: IntegrationType;
  status?: IntegrationStatus;
  isActive?: boolean;
  category?: IntegrationCategory;
  moduleIds?: string[];
  providerKey?: string;
  capability?: string;
  onlyActive?: boolean;
  instanceStatus?: IntegrationInstanceStatus;
}

export interface IntegrationConfigField {
  type: 'string' | 'number' | 'boolean' | 'object' | 'array';
  required: boolean;
  secret?: boolean;
  default?: any;
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    enum?: any[];
    custom?: (value: any) => boolean | string;
  };
  description?: string;
  label?: string;
  placeholder?: string;
}

export interface IntegrationConfigSchema {
  [key: string]: IntegrationConfigField;
}

export interface IntegrationMetadata {
  key: string;
  name: string;
  description: string;
  category: IntegrationCategory;
  type: IntegrationType;
  icon?: string;
  documentationUrl?: string;
  requiredConfig: IntegrationConfigSchema;
  capabilities: string[];
  isSystemIntegration: boolean;
  version?: string;
  tags?: string[];
}

export interface IntegrationFilters {
  search?: string;
  type?: IntegrationType;
  status?: IntegrationStatus;
  isActive?: boolean;
  instanceStatus?: IntegrationInstanceStatus;
}

export interface IntegrationStats {
  total: number;
  active: number;
  inactive: number;
  error: number;
  byType: Record<string, number>;
}

export interface PaginatedResponse<T> {
  data: T[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface IntegrationsState {
  integrations: Integration[];
  currentIntegration: Integration | null;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: IntegrationFilters;
  stats: IntegrationStats | null;
}

export interface TestConnectionResponse {
  success: boolean;
  message: string;
  error?: string;
}

export interface IntegrationCredentials {
  id: string;
  instanceStatus: IntegrationInstanceStatus;
  technicalConfig?: Record<string, any>;
  businessConfig?: Record<string, any>;
  apiKey?: string;
  apiSecret?: string;
  configVersion?: string;
  configuredAt?: string;
  configuredBy?: string;
}
